export class ApiError extends Error {
  readonly kind = "api" as const;

  constructor(
    readonly code: string,
    message: string,
    readonly status: number,
    readonly requestId?: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

export class NetworkError extends Error {
  readonly kind = "network" as const;

  constructor(message = "Network request failed") {
    super(message);
    this.name = "NetworkError";
  }
}

export class UnexpectedResponseError extends Error {
  readonly kind = "unexpected" as const;

  constructor(message: string) {
    super(message);
    this.name = "UnexpectedResponseError";
  }
}

export type ClientError = ApiError | NetworkError | UnexpectedResponseError;

export function isApiError(error: unknown): error is ApiError {
  return error instanceof ApiError;
}

export function isNetworkError(error: unknown): error is NetworkError {
  return error instanceof NetworkError;
}

export function userFacingMessage(error: unknown): string {
  if (error instanceof ApiError) {
    if (error.code === "INVALID_CREDENTIALS") {
      return "Неверный логин или пароль.";
    }
    if (error.code === "DEMO_PASSWORD_CHANGE_REJECTED") {
      return "Смена пароля недоступна в демо-режиме.";
    }
    if (error.code === "AUTH_REQUIRED") {
      return "Сессия истекла. Войдите снова.";
    }
    if (error.code === "COLLECTION_CONFLICT") {
      return "Коллекция была изменена. Показаны актуальные данные — повторите действие.";
    }
    if (error.code === "IMPORT_UNKNOWN_RELEASE") {
      return "Импорт отклонён: неизвестные релизы в файле.";
    }
    if (error.code === "INVALID_REQUEST" || error.code === "INVALID_CURSOR") {
      return "Некорректный запрос. Проверьте параметры поиска.";
    }
    if (error.code === "DEMO_UPLOAD_REJECTED") {
      return "Загрузка фото недоступна в демо-режиме.";
    }
    if (error.code === "UNSUPPORTED_IMAGE_FORMAT") {
      return error.message || "Этот формат фото пока не поддерживается. Выберите JPEG/PNG/WebP.";
    }
    if (error.code === "INVALID_IMAGE") {
      return error.message || "Файл не является поддерживаемым изображением.";
    }
    if (error.code === "IMAGE_TOO_LARGE") {
      return "Файл слишком большой. Выберите фото меньше 20 МБ.";
    }
    if (error.code === "IMAGE_PIXEL_LIMIT") {
      return "Изображение слишком большое по числу пикселей.";
    }
    if (error.code === "USER_IMAGE_LIMIT") {
      return "Для этого релиза уже загружено максимум фотографий.";
    }
    if (error.code === "RELEASE_NOT_FOUND") {
      return "Релиз не найден.";
    }
    if (error.code === "IMAGE_STORE_FULL") {
      return error.message || "Недостаточно места на сервере для сохранения фото.";
    }
    if (error.code === "IMAGE_STORE_UNAVAILABLE") {
      return "Загрузка фото временно недоступна.";
    }
    if (error.code === "INTERNAL_ERROR") {
      return "Сервер временно недоступен. Попробуйте позже.";
    }
    return "Не удалось выполнить запрос. Попробуйте позже.";
  }
  if (error instanceof NetworkError) {
    return "Нет соединения с сервером. Проверьте сеть.";
  }
  if (error instanceof UnexpectedResponseError) {
    return "Получен неожиданный ответ сервера.";
  }
  return "Произошла непредвиденная ошибка.";
}
